import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
} from "lift-ui";
import { Loader2 } from "lucide-react";
import StationForm, { StationFormData } from "../StationForm/StationForm";

interface CreateStationDialogProps {
  open: boolean;
  onClose: () => void;
  formData: StationFormData;
  onChange: (field: keyof StationFormData, value: string) => void;
  onSubmit: () => void;
  isVersionCreation?: boolean;
  loadingOverride?: boolean; // optional external control
}

const CreateStationDialog: React.FC<CreateStationDialogProps> = ({
  open,
  onClose,
  formData,
  onChange,
  onSubmit,
  isVersionCreation = false,
  loadingOverride,
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [showValidation, setShowValidation] = React.useState(false);
  const [isPreviewDropdownOpen, setIsPreviewDropdownOpen] = React.useState(false);

  // Check if at least one starter has text entered
  const hasValidStarter = formData.meta_config?.base_prompts?.some(
    (starter: any) => starter.text && starter.text.trim().length > 0
  );

  const isFormValid = formData.name && (hasValidStarter || formData.system_prompt);
  const title = isVersionCreation ? "Create New Station Version" : "Create New Station";
  const buttonText = isVersionCreation ? "Create Version" : "Create Station";

  // Reset validation state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setShowValidation(false);
    }
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-4xl flex flex-col"
        onInteractOutside={(e: any) => {
          if (isPreviewDropdownOpen) e.preventDefault();
        }}
        onPointerDownOutside={(e: any) => {
          if (isPreviewDropdownOpen) e.preventDefault();
        }}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-4">
          {/* Only the Form now; preview is handled inside the form */}
          <StationForm
            formData={formData}
            onChange={onChange}
            disableName={isVersionCreation}
            showValidation={showValidation}
            onPreviewDropdownOpenChange={setIsPreviewDropdownOpen}
          />
        </div>

        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!isFormValid) {
                setShowValidation(true);
                return;
              }

              setSubmitting(true);
              try {
                await onSubmit();
                // Close the dialog only when the API call succeeds
                onClose();
              } catch (error) {
                // You can handle error feedback here if desired
                console.error(error);
              } finally {
                setSubmitting(false);
              }
            }}
            disabled={submitting || loadingOverride}
            variant="default"
          >
            {submitting || loadingOverride ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              buttonText
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default CreateStationDialog;
